/**
 * ResponsiveV12Modal
 * -----------------------------------------------------------------------------
 * This component was created to display a responsive modal popup for V12 finance/payment offers
 * on the product details page. It dynamically renders content such as headings, images, videos,
 * GIFs, offer blocks, and terms & conditions based on the provided configuration and locale.
 * The modal adapts its layout for mobile and desktop, and supports dynamic media types and
 * localized content, providing users with detailed information about available payment options.
 * Data for this modal is fetched from Amplience CMS, allowing for flexible and dynamic content management.
 */

import Modal from 'components/commercetools-ui/atoms/modal';
import React, { useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';

const ResponsiveV12Modal = ({ v12, v12Modal, setV12Modal }: { v12: any; v12Modal: any; setV12Modal: any }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const paymentOverlayContentRef = useRef<HTMLIFrameElement | null>(null);
  useEffect(() => {
    const updateSize = () => setIsMobile(window.innerWidth <= 768);
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  const selectedLocale = cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU';
  const contentItem = v12?.contents?.find((item: any) => item?.screentype === (isMobile ? 'mobile' : 'desktop'));
  if (!contentItem) return null;

  const heading = contentItem?.modalHeading;
  const subHeading = contentItem?.modalSubHeading;
  const headingText = heading?.[selectedLocale];
  const subHeadingText = subHeading?.[selectedLocale];
  const headingStyle = {
    color: heading?.textColour || '#000',
    fontWeight: heading?.fontWeight || 'normal',
    fontSize: `${heading?.textSize || 17}px`,
  };
  const subHeadingStyle = {
    color: subHeading?.textColour || '#000',
    fontWeight: subHeading?.fontWeight || 'normal',
    fontSize: `${subHeading?.textSize || 14}px`,
  };
  const imgBgColor = {
    backgroundColor: contentItem?.imgContainer?.backgroundColor || '#f2f2f2',
  };
  const discription = contentItem?.payInDescription?.[selectedLocale] || '';
  const discriptionStyle = {
    color: contentItem?.payInDescription?.textColour || '#000',
    fontWeight: contentItem?.payInDescription?.fontWeight || 'normal',
    fontSize: `${contentItem?.payInDescription?.textSize || 13}px`,
  };

  const termsAndConditions = contentItem?.termsAndCondtions;

  const termsAndConditionsStyle = {
    backgroundColor: termsAndConditions?.backgroundColour || '#f2f2f2',
    color: termsAndConditions?.textColour || '#000',
    fontWeight: termsAndConditions?.fontWeight || 'normal',
    fontSize: `${termsAndConditions?.textSize || 12}px`,
  };

  const additionalText = termsAndConditions?.additionalText;
  const additionalInfo = additionalText?.additionalInfo;
  const additionalStyle = {
    fontWeight: additionalInfo?.linkTextFont || '400',
    fontSize: `${additionalInfo?.linkTextSize || 12}px`,
    color: additionalInfo?.linkTextColour || '#000',
  };

  const additionalTextStyle = {
    fontWeight: termsAndConditions?.additionalFontWeight || '400',
    fontSize: `${termsAndConditions?.textSize || 12}px`,
    color: termsAndConditions?.textColour || '#000',
  };
  const copyRightsStyle = {
    color: contentItem?.copyRights?.textColour || '#000',
    fontWeight: contentItem?.copyRights?.fontWeight || 'normal',
    fontSize: `${contentItem?.copyRights?.textSize || 10}px`,
  };
  const mediaType = contentItem?.imgContainer?.media;
  let payInMedia;

  if (mediaType === 'image') {
    payInMedia = contentItem?.imgContainer?.payInImage;
  } else if (mediaType === 'gif') {
    payInMedia = contentItem?.imgContainer?.payInGif;
  } else if (mediaType === 'video') {
    payInMedia = contentItem?.imgContainer?.payInVideo;
  } else {
    payInMedia = null;
  }

  return (
    <Modal
      isOpen={v12Modal}
      onRequestClose={() => {
        setV12Modal(false);
        paymentOverlayContentRef.current = null;
      }}
      style={{
        content: {
          width: '512px',
          minHeight: '665px',
          overflowY: isMobile ? 'scroll' : 'visible',
          padding: '0',
          borderRadius: '8px',
        },
      }}
    >
      <section className="relative flex flex-col px-24 py-20">
        <div className="absolute right-0 top-0 rounded-bl-lg rounded-tr-lg bg-black ">
          <button id="close-button" className="p-6 md:p-8" onClick={() => setV12Modal(false)}>
            <svg width="8" height="9" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8 1.30571L7.19429 0.5L4 3.69429L0.805714 0.5L0 1.30571L3.19429 4.5L0 7.69429L0.805714 8.5L4 5.30571L7.19429 8.5L8 7.69429L4.80571 4.5L8 1.30571Z"
                fill="white"
              />
            </svg>
          </button>
        </div>

        <header className="mb-4 mt-[1rem] flex ">
          <div id="modal-heading-section" className="mb-6 ">
            <p id="modalHeading" className="font-body leading-6" style={headingStyle}>
              {headingText}
            </p>
            <p id="modalSubHeading" className="font-body leading-6" style={subHeadingStyle}>
              {subHeadingText}
            </p>
          </div>
        </header>
        <div className="h-[69px] w-full" style={imgBgColor}>
          <div id="v12Logo" className="flex items-center justify-center py-10">
            {payInMedia && mediaType === 'image' && (
              <img
                src={`https://${payInMedia?.endpoint}.a.bigcontent.io/v1/static/${payInMedia?.name}`}
                alt={payInMedia?.name}
              />
            )}
            {payInMedia && mediaType === 'gif' && (
              <img
                src={`https://${payInMedia?.defaultHost}/i/${payInMedia?.endpoint}/${payInMedia?.name}`}
                alt={payInMedia?.name}
              />
            )}
            {payInMedia && mediaType === 'video' && (
              <video
                src={`https://${payInMedia?.defaultHost}/v/${payInMedia?.endpoint}/${payInMedia?.name}/mp4_720p`}
                loop
                autoPlay
                muted
                playsInline
                className="h-50 w-full object-cover p-10"
              />
            )}
          </div>
        </div>
        <section id="modal-discription" className="py-14">
          <p style={discriptionStyle}>{discription}</p>
        </section>
        <section className="flex flex-row gap-x-16">
          {contentItem &&
            contentItem?.offers &&
            contentItem?.offers?.length > 0 &&
            contentItem?.offers?.map((offer: any, index: number) => {
              const locale = selectedLocale || 'en-GB';

              const spendOverText = offer?.spendOver?.[locale];
              const spendOverLabel = offer?.spendOver?.spendText?.[locale] || 'SPEND OVER';

              const spendOverBlockStyle = {
                backgroundColor: offer?.spendOver?.backgroundColor || '#F2F2F2',
                color: offer?.spendOver?.textColour || '#000',
                fontSize: `${offer?.spendOver?.textSize || 45}px`,
                fontWeight: offer?.spendOver?.fontWeight || 600,
              };

              const spendOverLabelStyle = {
                color: offer?.spendOver?.spendText?.spendOverTextColour || '#1E1E1E',
                fontSize: `${offer?.spendOver?.spendText?.spendOverTextSize || 16}px`,
                fontWeight: offer?.spendOver?.spendText?.spendOverFontWeight || 500,
              };

              const topContent = offer?.interestFree?.content?.topContent?.[locale] || 'Get';
              const interestFreeValue = offer?.interestFree?.[locale];
              const bottomContent = offer?.interestFree?.content?.bottomContent?.[locale] || 'Interest Free Credit';

              const interestFreeBlockStyle = {
                backgroundColor: offer?.interestFree?.backgroundColor || '#FFF',
              };

              const topContentStyle = {
                fontWeight: offer?.interestFree?.content?.fontWeight || '400',
                fontSize: `${offer?.interestFree?.content?.textSize || 16}px`,
                color: offer?.interestFree?.content?.textColour || '#000',
              };

              const interestFreeValueStyle = {
                fontWeight: offer?.interestFree?.fontWeight || '700',
                fontSize: `${offer?.interestFree?.textSize || 20}px`,
                color: offer?.interestFree?.textColour || '#000',
              };

              const bottomContentStyle = {
                fontWeight: offer?.interestFree?.content?.fontWeight || '400',
                fontSize: `${offer?.interestFree?.content?.textSize || 16}px`,
                color: offer?.interestFree?.content?.textColour || '#000',
              };
              return (
                <article id="offer-section" key={index} className="flex w-[230px] flex-wrap border border-[#F2F2F2] ">
                  <div
                    className="flex w-full flex-col px-10 pb-10 pt-10 text-center font-body"
                    style={spendOverBlockStyle}
                  >
                    <p style={spendOverLabelStyle}>{spendOverLabel}</p>
                    <p>{spendOverText}</p>
                  </div>

                  <div
                    className="flex w-full flex-col px-10 pb-10 pt-8 text-center font-body"
                    style={interestFreeBlockStyle}
                  >
                    <p style={topContentStyle}>{topContent}</p>
                    <p style={interestFreeValueStyle}>{interestFreeValue}</p>
                    <p style={bottomContentStyle}>{bottomContent}</p>
                  </div>
                </article>
              );
            })}
        </section>
      </section>
      <section
        id="terms-and-conditions"
        className="flex w-full flex-col items-start justify-center"
        style={{ backgroundColor: termsAndConditionsStyle?.backgroundColor }}
      >
        <div className="px-24 pt-14">
          {termsAndConditions?.points?.length > 0 && (
            <article className="font-body leading-5">
              <p style={termsAndConditionsStyle}> {termsAndConditions?.heading?.[selectedLocale]}</p>
              <ul className="py-10 pl-16">
                {termsAndConditions?.points?.map((point: any, idx: number) => (
                  <li key={idx} className=" list-disc" style={termsAndConditionsStyle}>
                    {point?.[selectedLocale]}
                  </li>
                ))}
              </ul>
            </article>
          )}
        </div>

        {additionalInfo && additionalText && (
          <div className="flex gap-x-2 px-24 pb-18">
            <span style={additionalTextStyle}>{additionalText?.[selectedLocale]}</span>
            <a
              href={termsAndConditions?.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center"
            >
              <span className="underline underline-offset-4" style={additionalStyle}>
                {additionalInfo?.[selectedLocale]}
              </span>
            </a>
          </div>
        )}
      </section>
      <footer id="modal-footer" className="flex w-full items-center px-22 py-10">
        <p id="copyRights" style={copyRightsStyle}>
          {contentItem?.copyRights?.[selectedLocale]}
        </p>
      </footer>
    </Modal>
  );
};

export default ResponsiveV12Modal;
